import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flag, Ban, ShieldCheck, ExternalLink, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Panel, SectionTitle, TactLoader } from "../components/Tactical";
import ProtectedImage from "../components/ProtectedImage";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

function whenAgo(iso) {
  if (!iso) return "—";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60000) return `${Math.max(1, Math.floor(ms / 1000))}s ago`;
    if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
    if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
    return `${Math.floor(ms / 86_400_000)}d ago`;
  } catch { return iso; }
}

/**
 * List of every player-on-player report. Both admins AND moderators can
 * view this — the screenshot evidence is critical context for any moderation
 * decision. Only ADMINS see the Ban / Unban action buttons; mods get
 * read-only access (with a hint to escalate to admin if they think a ban
 * is warranted).
 */
export default function PlayerReportsTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/player-reports");
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setRows([]);
    }
  };
  useEffect(() => { refresh(); }, []);

  const ban = async (userId, username) => {
    const reason = window.prompt(`Ban ${username}? Optionally add a reason:`, "");
    if (reason === null) return; // cancelled
    setBusyId(userId);
    try {
      await api.post(`/admin/users/${userId}/ban`, { reason });
      toast.success(`${username} banned.`);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const unban = async (userId, username) => {
    if (!window.confirm(`Unban ${username}? They'll be able to log in and play again.`)) return;
    setBusyId(userId);
    try {
      await api.post(`/admin/users/${userId}/unban`);
      toast.success(`${username} unbanned.`);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="player-reports-tab">
      <SectionTitle kicker="// player reports" testId="player-reports-title">Player Reports</SectionTitle>

      {rows === null ? (
        <TactLoader />
      ) : rows.length === 0 ? (
        <Panel className="font-mono text-[#71717A] text-sm" testId="player-reports-empty">
          [ NO REPORTS FILED YET — your community is clean, chief. ]
        </Panel>
      ) : (
        <ul className="space-y-2" data-testid="player-reports-list">
          {rows.map((r) => (
            <li
              key={r.id}
              data-testid={`report-row-${r.id}`}
              className={`bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] border-l-2 px-4 py-3 ${
                r.reported.banned ? "border-l-[#E11D48]" : "border-l-[#EA580C]"
              }`}
            >
              <div className="flex items-start gap-3 flex-wrap">
                <Flag className={`w-4 h-4 flex-shrink-0 mt-0.5 ${r.reported.banned ? "text-[#E11D48]" : "text-[#EA580C]"}`} />
                <div className="flex-1 min-w-0">
                  {/* Header: reporter → reported */}
                  <div className="font-heading text-base uppercase tracking-wider text-[#09090B] dark:text-[#F4F4F5] flex items-center gap-2 flex-wrap">
                    <span className="text-[#71717A]">{r.reporter.username || "—"}</span>
                    <span className="text-[#71717A] font-mono text-xs">reported</span>
                    <Link
                      to={`/players/${encodeURIComponent(r.reported.username || "")}`}
                      data-testid={`report-open-profile-${r.reported.id}`}
                      className="text-[#7C3AED] hover:underline inline-flex items-center gap-1"
                    >
                      {r.reported.username || "—"} <ExternalLink className="w-3 h-3" />
                    </Link>
                    {r.reported.banned && (
                      <span className="font-mono text-[9px] uppercase tracking-[0.3em] bg-[#E11D48] text-white px-1.5 py-0.5" data-testid={`report-banned-${r.reported.id}`}>
                        BANNED
                      </span>
                    )}
                    {r.reported.role === "admin" && (
                      <span className="font-mono text-[9px] uppercase tracking-[0.3em] bg-[#7C3AED] text-white px-1.5 py-0.5">ADMIN</span>
                    )}
                  </div>
                  {/* Reason */}
                  {r.reason && (
                    <div className="font-body text-sm text-[#09090B] dark:text-[#F4F4F5] mt-1 pl-1 border-l-2 border-l-[#E4E4E7] dark:border-l-[#2A2A30]">
                      “{r.reason}”
                    </div>
                  )}
                  {/* Screenshot evidence */}
                  {r.screenshot_path && (
                    <ProtectedImage
                      path={r.screenshot_path}
                      alt="Report evidence"
                      testId={`report-screenshot-${r.id}`}
                    />
                  )}
                  {/* Meta */}
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] mt-1.5">
                    {r.league_name || "—"} · {whenAgo(r.created_at)}
                  </div>
                </div>

                {/* Actions: ban / unban (admin only) */}
                <div className="flex-shrink-0">
                  {!isAdmin ? (
                    <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#71717A]" data-testid={`mod-readonly-${r.reported.id}`}>read-only · escalate to admin</span>
                  ) : r.reported.role === "admin" ? (
                    <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#71717A]">admin protected</span>
                  ) : r.reported.banned ? (
                    <button
                      type="button"
                      onClick={() => unban(r.reported.id, r.reported.username)}
                      disabled={busyId === r.reported.id}
                      data-testid={`unban-btn-${r.reported.id}`}
                      className="inline-flex items-center gap-1.5 border border-[#34C759] text-[#34C759] font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 hover:bg-[#34C759] hover:text-white transition-colors disabled:opacity-50"
                    >
                      {busyId === r.reported.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
                      Unban
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => ban(r.reported.id, r.reported.username)}
                      disabled={busyId === r.reported.id}
                      data-testid={`ban-btn-${r.reported.id}`}
                      className="inline-flex items-center gap-1.5 border border-[#E11D48] text-[#E11D48] font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 hover:bg-[#E11D48] hover:text-white transition-colors disabled:opacity-50"
                    >
                      {busyId === r.reported.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Ban className="w-3 h-3" />}
                      Ban Player
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
